import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { Device } from '@capacitor/device';
import { differenceInYears } from 'date-fns';
import { MetaAppEvents, type MetaTrackingStatus } from '@/plugins/meta-app-events';
import { getConsentChoice, type ConsentChoice } from '@/services/privacy-consents';

/**
 * Meta App Events (SDK) for Liel's six business actions.
 *
 * Off unless VITE_META_APP_EVENTS_ENABLED=true, a Meta App ID and client token
 * are configured, the app runs natively, AND the user opted in. Payloads are
 * privacy-minimized: event name, content type and event ID only — never
 * content names, fitness/health data or onboarding answers.
 *
 * CompleteRegistration and workout level-achieved (fb_mobile_level_achieved) are
 * also sent by the server (Conversions API) under the same name and event ID, so
 * Meta deduplicates them.
 */

export const MetaEventName = {
  completeRegistration: 'CompleteRegistration',
  viewContent: 'ViewContent',
  achieveLevel: 'fb_mobile_level_achieved',
} as const;
export type MetaEventName = (typeof MetaEventName)[keyof typeof MetaEventName];

export const MetaContentType = { workout: 'workout', exercise: 'exercise' } as const;
export type MetaContentType = (typeof MetaContentType)[keyof typeof MetaContentType];

// SDK parameter keys. Confirm in Events Manager > Test Events before enabling.
export const META_PARAM_CONTENT_TYPE = 'fb_content_type';
export const META_PARAM_EVENT_ID = 'event_id';

/** Ad measurement is offered only to users whose self-declared date of birth makes them adults. */
export const META_MIN_CONSENT_AGE = 18;
const CONTEXT_TIMEOUT_MS = 500;

// Consent is stored per account by the backend (services/privacy-consents), never on the device.
export type MetaConsentState = ConsentChoice;
/** The signed-in account, as far as Meta consent is concerned. Only the birth date is read, locally. */
export type MetaAccount = { id: string; profile?: { dateOfBirth?: string | null } | null };

/** Sent with registration / workout-finish requests (see gyming-back meta-app-context). */
export type MetaAppContext = {
  consent: true;
  platform: 'ios' | 'android';
  osVersion: string;
  appId?: string;
  appVersion?: string;
  appBuild?: string;
  anonId?: string;
  advertiserTrackingEnabled: boolean;
  applicationTrackingEnabled: boolean;
};

type MetaEnv = Partial<Record<'VITE_META_APP_EVENTS_ENABLED' | 'VITE_META_APP_ID' | 'VITE_META_CLIENT_TOKEN', string>>;

export const getMetaConfig = (env: MetaEnv = import.meta.env as MetaEnv) => {
  const appId = env.VITE_META_APP_ID?.trim() || '';
  const clientToken = env.VITE_META_CLIENT_TOKEN?.trim() || '';
  return {
    enabled: env.VITE_META_APP_EVENTS_ENABLED === 'true' && Boolean(appId) && Boolean(clientToken),
    appId,
    clientToken,
  };
};

const getPlatform = () => Capacitor.getPlatform();

/** Feature available on this build/device (used to show the consent setting). */
export const isMetaAvailable = () => Capacitor.isNativePlatform() && getMetaConfig().enabled;

/** True only for a valid birth date at least META_MIN_CONSENT_AGE years ago. */
export const isMetaConsentAgeEligible = (dateOfBirth?: string | null, now = new Date()): boolean => {
  if (!dateOfBirth) return false;
  const dob = new Date(dateOfBirth);
  if (Number.isNaN(dob.getTime()) || dob > now) return false;
  return differenceInYears(now, dob) >= META_MIN_CONSENT_AGE;
};

let account: { id: string; dateOfBirth: string | null; ageEligible: boolean } | null = null;
// CompleteRegistration of an account created in this session, held until its owner chooses.
let pendingRegistration: { accountId: string; eventId: string } | null = null;

/** The stored choice of the signed-in account ('unset' when signed out or never asked). */
export const getMetaConsentState = async (): Promise<MetaConsentState> =>
  account ? getConsentChoice(account.id, 'metaAdMeasurement') : 'unset';

/** Effective consent: the signed-in, age-eligible account granted it. */
export const getMetaConsent = async (): Promise<boolean> =>
  Boolean(account?.ageEligible) && (await getMetaConsentState()) === 'granted';

const getTrackingStatus = async (): Promise<MetaTrackingStatus> => {
  if (getPlatform() !== 'ios') return 'notApplicable';
  return (await MetaAppEvents.getTrackingAuthorizationStatus()).status;
};

// iOS: the IDFA requires ATT authorization. Android: the user's in-app opt-in.
const isAdvertiserTrackingEnabled = (status: MetaTrackingStatus) =>
  getPlatform() === 'ios' ? status === 'authorized' : true;

let initPromise: Promise<boolean> | null = null;
// The native SDK may be running (initialize resolved at least once in this JS session).
let sdkStarted = false;
// Consent was revoked in this app session: nothing resumes until the next launch, even if
// consent is granted again. The native plugin enforces the same per process.
let revokedThisSession = false;

/** Initializes the SDK and measures the install / app activation. Safe to call repeatedly. */
export const initMetaAppEvents = (): Promise<boolean> => {
  if (revokedThisSession) return Promise.resolve(false);
  if (!initPromise) {
    initPromise = (async () => {
      if (!isMetaAvailable() || !(await getMetaConsent())) return false;
      const { appId, clientToken } = getMetaConfig();
      const status = await getTrackingStatus();
      await MetaAppEvents.initialize({
        appId,
        clientToken,
        advertiserIdCollectionEnabled: isAdvertiserTrackingEnabled(status),
      });
      sdkStarted = true;
      await MetaAppEvents.activateApp();
      return true;
    })().catch(() => {
      initPromise = null;
      return false;
    });
  }
  return initPromise;
};

/** App returned to foreground: count the activation (install attribution / sessions). */
export const onMetaAppActive = async () => {
  try {
    if (!revokedThisSession && initPromise && (await initPromise) && (await getMetaConsent())) {
      await MetaAppEvents.activateApp();
    }
  } catch {
    // Analytics must never break the app.
  }
};

/**
 * Revocation: stop uploads before anything else, without a final flush. The SDK cannot be
 * shut down or have its queue cleared (see docs/META_CONSENT_REVOCATION.md).
 */
const stopMetaTransmission = async () => {
  revokedThisSession = true;
  const pending = initPromise;
  initPromise = null;
  if (pending) await pending.catch(() => false); // let an in-flight initialize settle first
  // Called even if this JS context never started the SDK: after a WebView reload (live
  // update) it may still be running natively. The plugin handles the not-started case.
  if (!Capacitor.isNativePlatform()) return;
  try {
    await MetaAppEvents.disableTransmission();
  } catch {
    // Logging is already blocked here and natively; never break the settings screen.
  }
};

/**
 * Applies the signed-in account's opt-in / opt-out to the SDK (Settings and the consent sheet).
 * The choice itself is stored by the backend: revoke here before saving, so transmission stops
 * immediately; opt in only after the grant was saved. Opting in on iOS asks for ATT once.
 * Revoking a granted consent lasts for the rest of the app session: granting again takes
 * effect from the next launch.
 */
export const setMetaConsent = async (granted: boolean): Promise<void> => {
  const current = account;
  if (!current) return;
  if (!granted) {
    if (sdkStarted || (await getMetaConsent())) await stopMetaTransmission();
    return;
  }
  if (revokedThisSession || !isMetaAvailable() || !current.ageEligible || !(await getMetaConsent())) return;
  try {
    if (getPlatform() === 'ios' && (await getTrackingStatus()) === 'notDetermined') {
      await MetaAppEvents.requestTrackingAuthorization();
    }
    initPromise = null;
    await initMetaAppEvents();
  } catch {
    // The saved consent stands even if the native call fails.
  }
};

// Whether the native SDK may be running in this process, even if this JS context did not
// start it (WebView reload, e.g. live update). getAnonymousId is empty until initialized.
const isNativeSdkRunning = async () => {
  if (sdkStarted) return true;
  if (!Capacitor.isNativePlatform()) return false;
  try {
    return Boolean((await MetaAppEvents.getAnonymousId()).anonId);
  } catch {
    return false;
  }
};

/**
 * Called whenever the signed-in user is known (or null on logout). An account never inherits
 * another account's consent: if the SDK is running and this account has not (effectively)
 * consented, transmission stops for the rest of the app session, exactly like a revocation.
 */
export const setMetaAccount = async (next: MetaAccount | null): Promise<void> => {
  const previousId = account?.id ?? null;
  // A response without a birth date cannot clear one already known for the same account: the
  // date picker cannot clear it, so it is an older profile (e.g. a refetch started before the
  // birth date was saved). A present birth date, including a minor's, always applies.
  const dateOfBirth = next?.profile?.dateOfBirth || (next?.id === previousId ? account?.dateOfBirth ?? null : null);
  account = next ? { id: next.id, dateOfBirth, ageEligible: isMetaConsentAgeEligible(dateOfBirth) } : null;
  if (pendingRegistration && pendingRegistration.accountId !== account?.id) pendingRegistration = null;
  if (!isMetaAvailable()) return;
  try {
    const pending = initPromise;
    if (pending) await pending.catch(() => false);
    if (await isNativeSdkRunning()) {
      if (!(await getMetaConsent())) await stopMetaTransmission();
    } else if (account && account.id !== previousId) {
      initPromise = null; // the launch-time attempt ran before the account was known
      await initMetaAppEvents();
    }
  } catch {
    // Analytics must never break sign-in.
  }
};

export const newMetaEventId = (): string =>
  typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;

const logMetaEvent = async (name: MetaEventName, eventId: string, contentType?: MetaContentType): Promise<boolean> => {
  try {
    if (!(await initMetaAppEvents()) || !(await getMetaConsent())) return false;
    if (revokedThisSession) return false; // revoked while the checks above were pending
    await MetaAppEvents.logEvent({
      name,
      params: {
        [META_PARAM_EVENT_ID]: eventId,
        ...(contentType ? { [META_PARAM_CONTENT_TYPE]: contentType } : {}),
      },
    });
    return true;
  } catch {
    return false;
  }
};

/**
 * Only after the server created the account; uses the server's event ID (shared with CAPI).
 * A brand-new account has not chosen yet, so the event is held until the consent sheet is
 * answered (see resolveMetaConsentOffer) and logged at most once.
 */
export const trackMetaRegistrationCompleted = async (serverEventId: string | undefined, newAccount: MetaAccount) => {
  if (!serverEventId) return false;
  await setMetaAccount(newAccount);
  const state = await getMetaConsentState();
  if (state === 'granted') return logMetaEvent(MetaEventName.completeRegistration, serverEventId);
  pendingRegistration = state === 'unset' ? { accountId: newAccount.id, eventId: serverEventId } : null;
  return false;
};

/** Offer the Meta choice (consent sheet, Settings)? Only on builds with Meta and to age-eligible users. */
export const isMetaConsentOffered = (dateOfBirth?: string | null): boolean =>
  isMetaAvailable() && isMetaConsentAgeEligible(dateOfBirth);

/**
 * The consent sheet's Meta choice was saved: apply it, then log the held registration once if
 * granted. A choice that was not saved must not be passed here.
 */
export const resolveMetaConsentOffer = async (granted: boolean): Promise<void> => {
  const held = pendingRegistration?.accountId === account?.id ? pendingRegistration : null;
  pendingRegistration = null;
  await setMetaConsent(granted);
  if (granted && held) await logMetaEvent(MetaEventName.completeRegistration, held.eventId);
};

export const trackMetaWorkoutStarted = () =>
  logMetaEvent(MetaEventName.viewContent, newMetaEventId(), MetaContentType.workout);

/** Only after the server confirmed a full completion; uses the server's event ID (shared with CAPI). */
export const trackMetaWorkoutCompleted = (serverEventId?: string) =>
  serverEventId
    ? logMetaEvent(MetaEventName.achieveLevel, serverEventId, MetaContentType.workout)
    : Promise.resolve(false);

export const trackMetaExerciseStarted = () =>
  logMetaEvent(MetaEventName.viewContent, newMetaEventId(), MetaContentType.exercise);

export const trackMetaExerciseCompleted = () =>
  logMetaEvent(MetaEventName.achieveLevel, newMetaEventId(), MetaContentType.exercise);

const withTimeout = <T>(promise: Promise<T>, ms: number, fallback: T): Promise<T> =>
  Promise.race([promise, new Promise<T>(resolve => setTimeout(() => resolve(fallback), ms))]);

const buildMetaAppContext = async (): Promise<MetaAppContext | undefined> => {
  if (!(await initMetaAppEvents()) || !(await getMetaConsent())) return undefined;
  const platform = getPlatform();
  if (platform !== 'ios' && platform !== 'android') return undefined;
  const [device, app, anon, status] = await Promise.all([
    Device.getInfo(),
    App.getInfo(),
    MetaAppEvents.getAnonymousId(),
    getTrackingStatus(),
  ]);
  // A denied/undetermined ATT on iOS must not be bypassed through the server (CAPI) copy.
  if (platform === 'ios' && status !== 'authorized') return undefined;
  return {
    consent: true,
    platform,
    osVersion: device.osVersion,
    appId: app.id || undefined,
    appVersion: app.version || undefined,
    appBuild: app.build || undefined,
    anonId: anon.anonId || undefined,
    advertiserTrackingEnabled: isAdvertiserTrackingEnabled(status),
    applicationTrackingEnabled: true,
  };
};

/** Context for the server's CAPI copy of an event; undefined when Meta is off or not consented. Never blocks for long. */
export const getMetaAppContext = (): Promise<MetaAppContext | undefined> =>
  withTimeout(
    buildMetaAppContext().catch(() => undefined),
    CONTEXT_TIMEOUT_MS,
    undefined
  );

/** Test-only reset of the module's init state. */
export const __resetMetaAppEventsForTests = () => {
  initPromise = null;
  account = null;
  pendingRegistration = null;
  sdkStarted = false;
  revokedThisSession = false;
};
